import {
  App,
  Alert,
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
  Typography,
} from "antd";
import { useState } from "react";
import { api, unwrap } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n, type Locale } from "@/i18n";

type Values = { name: string; locale: Locale };

export function CompanySettings() {
  const { t, formatDate, formatDateTime } = useI18n();
  const { me, refetchMe } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<Values>();
  const [saving, setSaving] = useState(false);
  const ws = me?.workspace;
  if (!ws) return null;

  const save = async (values: Values) => {
    setSaving(true);
    try {
      await unwrap(api.PATCH("/v1/workspace", { body: values }));
      await refetchMe();
      void message.success(t("settings.company.saved"));
    } catch (err) {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Typography.Title level={2} style={{ marginTop: 0, fontSize: 28 }}>
        {t("settings.company.title")}
      </Typography.Title>
      <Card style={{ maxWidth: 640 }}>
        <Typography.Paragraph>
          {t("settings.company.plan")}:{" "}
          <Typography.Text strong>{t(`plan.${ws.plan}`)}</Typography.Text>
        </Typography.Paragraph>
        {ws.plan === "trial" && ws.trialEndsAt && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
            title={t("settings.company.trialUntil", {
              date: formatDate(ws.trialEndsAt, ws.timezone),
            })}
          />
        )}
        <Form
          form={form}
          layout="vertical"
          initialValues={{ name: ws.name, locale: ws.locale }}
          onFinish={save}
          disabled={saving}
        >
          <Form.Item
            name="name"
            label={t("settings.company.name")}
            rules={[
              {
                required: true,
                whitespace: true,
                min: 2,
                message: t("field.too_short"),
              },
              { max: 120, message: t("field.too_long") },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="locale"
            label={t("settings.company.locale")}
            extra={t("settings.company.localeHelp")}
          >
            <Select
              options={[
                { value: "id", label: t("lang.id") },
                { value: "en", label: t("lang.en") },
              ]}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>
            {t("common.save")}
          </Button>
        </Form>
      </Card>
      <SupportAccessCard
        until={ws.supportAccessUntil}
        timezone={ws.timezone}
        formatDateTime={formatDateTime}
      />
    </>
  );
}

/**
 * Akses support Zerpid (Plan §6.16): the owner lets Zerpid support look
 * inside, read-only, for 24 or 72 hours, and can withdraw it at any time.
 * Each support session shows in the activity log.
 */
function SupportAccessCard({
  until,
  timezone,
  formatDateTime,
}: {
  until: string | null;
  timezone: string;
  formatDateTime: (v: string, tz?: string) => string;
}) {
  const { t } = useI18n();
  const { refetchMe } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const [busy, setBusy] = useState<number | null>(null);
  const [now] = useState(() => Date.now());
  const open = until !== null && new Date(until).getTime() > now;
  const set = async (hours: 0 | 24 | 72) => {
    setBusy(hours);
    try {
      await unwrap(
        api.PUT("/v1/workspace/support-access", { body: { hours } }),
      );
      await refetchMe();
      void message.success(
        t(hours ? "settings.support.granted" : "settings.support.withdrawn"),
      );
    } catch (err) {
      void message.error(errorText(err));
    } finally {
      setBusy(null);
    }
  };
  return (
    <Card
      title={t("settings.support.title")}
      style={{ maxWidth: 640, marginTop: 16 }}
    >
      <Typography.Paragraph type="secondary">
        {t("settings.support.help")}
      </Typography.Paragraph>
      {open && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={t("settings.support.openUntil", {
            at: formatDateTime(until!, timezone),
          })}
        />
      )}
      <Space wrap>
        <Button loading={busy === 24} onClick={() => void set(24)}>
          {t("settings.support.grant24")}
        </Button>
        <Button loading={busy === 72} onClick={() => void set(72)}>
          {t("settings.support.grant72")}
        </Button>
        {open && (
          <Button danger loading={busy === 0} onClick={() => void set(0)}>
            {t("settings.support.withdraw")}
          </Button>
        )}
      </Space>
    </Card>
  );
}
